import React, { useEffect, useState, useRef } from "react";
import { Autocomplete, TextField, CircularProgress, SxProps, Theme } from "@mui/material";

interface ByzAsynchronousSelectV2Props<T> {
  fetchOptions?: () => Promise<T[]>; // A function that returns a Promise of an array of your data type (optional if preLoadedOptions is provided)
  label: string;
  onChange: (value: any) => void;
  value?: any;
  multiple?: boolean;
  selectLabel?: string; // The key in your data object to display as the label
  preLoadedOptions?: T[]; // Pre-loaded options to avoid API calls
  [key: string]: any; // Allows for other props to be passed down to Autocomplete
  sx?: SxProps<Theme>;
  size?: "small" | "medium";
}

const ByzAsynchronousSelectV2 = <T extends Record<string, any>>({
  fetchOptions,
  label,
  onChange,
  value,
  multiple,
  selectLabel = "",
  preLoadedOptions,
  sx,
  size = "small",
  placeholder,
  ...rest
}: ByzAsynchronousSelectV2Props<T>) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedValue, setSelectedValue] = useState<any>(null);
  const fetchOptionsRef = useRef(fetchOptions);
  const hasLoadedRef = useRef(false);

  // fetchOptions referansını güncelle ama API çağrısı yapma
  useEffect(() => {
    fetchOptionsRef.current = fetchOptions;
  }, [fetchOptions]);

  const defaultFormControlSx: SxProps<Theme> = {
    "& .MuiInputLabel-root": {
      color: "#2498f3 !important",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#2498f3 !important",
    },
    "& .MuiFormLabel-root": {
      color: "#2498f3 !important",
    },
    "& .MuiFormLabel-root.Mui-focused": {
      color: "#2498f3 !important",
    },
    "& .MuiSelect-root": {
      height: size === "small" ? "36px" : "auto",
    },
    "& .MuiOutlinedInput-root": {
      height: size === "small" ? "36px" : "auto",
    },
  };

  const defaultSelectSx: SxProps<Theme> = {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2498f3",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2498f3",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2498f3",
    },
  };

  const mergedSx = { ...defaultFormControlSx, ...defaultSelectSx, ...sx };

  useEffect(() => {
    let active = true;

    const loadData = async () => {
      if (preLoadedOptions && Array.isArray(preLoadedOptions)) {
        const formattedData = preLoadedOptions.map((item: T) => {
          let displayLabel = item["name"] || "";
          if (selectLabel) {
            displayLabel = selectLabel
              .split(",")
              .map((key) => item[key.trim()] || "")
              .join(" ")
              .trim();
          }

          const formatted = {
            label: displayLabel,
            value: item["id"],
            ...item,
          };
          return formatted;
        });
        setOptions(formattedData);
        setIsLoading(false);
        return;
      }

      if (!fetchOptions) {
        setOptions([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const res = await fetchOptions();

        if (active) {
          if (Array.isArray(res) && res.length > 0) {
            const formattedData = res.map((item: T) => {
              let displayLabel = item["name"];
              if (selectLabel) {
                displayLabel = selectLabel
                  .split(",")
                  .map((key) => item[key.trim()] || "")
                  .join(" ")
                  .trim();
              }

              const formatted = {
                label: displayLabel,
                value: item["id"],
                ...item,
              };
              return formatted;
            });
            setOptions(formattedData);
          } else {
            setOptions([]);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setOptions([]);
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      active = false;
    };
  }, [fetchOptions, preLoadedOptions]);

  useEffect(() => {
    if (value !== selectedValue) {
      setSelectedValue(value);
    }
  }, [value, selectedValue]);

  const handleChange = (
    event: React.SyntheticEvent<Element, Event>,
    newValue: any
  ) => {
    setSelectedValue(newValue);
    onChange(newValue);
  };

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      multiple={multiple}
      options={options}
      loading={isLoading}
      onChange={handleChange}
      value={selectedValue || null}
      size={size}
      sx={mergedSx}
      getOptionLabel={(option) => {
        if (typeof option === "string") return option;

        if (option && typeof option === "object") {
          if (option.label) return option.label;

          if (selectLabel) {
            const labelValue = selectLabel
              .split(",")
              .map((key) => option[key.trim()] || "")
              .join(" ")
              .trim();
            if (labelValue) return labelValue;
          }

          if (option.name) return option.name;

          if (option.id) {
            if (options.length > 0) {
              const foundOption = options.find(
                (opt) => String(opt.id) === String(option.id)
              );
              if (foundOption) {
                return foundOption.label || foundOption.name || "";
              }
            }
            return "";
          }
        }
        return "";
      }}
      isOptionEqualToValue={(option, value) => {
        if (!option || !value) return false;
        const optionId = option.id || option.value;
        const valueId = value.id || value.value;
        return String(optionId) === String(valueId);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          placeholder={placeholder}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      {...rest}
    />
  );
};

export default ByzAsynchronousSelectV2;
